import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';

interface PreloadOptions {
  preloadRoutes?: boolean;
  preloadAudio?: boolean;
  preloadBible?: boolean;
  preloadPrayers?: boolean;
}

const PRELOAD_ROUTES = [
  { path: '/bible', priority: 'high' },
  { path: '/prayers', priority: 'high' },
  { path: '/profile', priority: 'medium' },
  { path: '/community', priority: 'low' }
] as const;

export const usePreloading = (options: PreloadOptions = {}) => {
  const { isAuthenticated, user } = useAuth();
  
  const {
    preloadRoutes = true,
    preloadAudio = true,
    preloadBible = true,
    preloadPrayers = true
  } = options;

  // Preload route chunks
  const preloadRouteChunks = useCallback(() => {
    if (!preloadRoutes) return;
    
    PRELOAD_ROUTES.forEach(({ path, priority }) => {
      // Use requestIdleCallback for low-priority preloading
      const preloadFn = () => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'fetch';
        link.href = path;
        document.head.appendChild(link);
      };

      if (priority === 'high') {
        preloadFn();
      } else if (priority === 'medium') {
        setTimeout(preloadFn, 1000);
      } else {
        setTimeout(preloadFn, 3000);
      }
    });
  }, [preloadRoutes]);

  // Preload Bible content for authenticated users
  const preloadBibleContent = useCallback(async () => {
    if (!preloadBible || !isAuthenticated) return;
    
    try {
      // Preload popular verses and chapters in the background
      const controller = new AbortController();
      
      // Example: Preload John 3:16, Psalm 23, etc.
      const popularReferences = ['John 3:16', 'Psalm 23:1', 'Romans 8:28'];
      
      popularReferences.forEach(async (reference) => {
        try {
          // This would integrate with your Bible API
          await fetch(`/api/bible/verse?reference=${encodeURIComponent(reference)}`, {
            signal: controller.signal
          });
        } catch (error) {
          // Silently fail for preloading
          console.debug('Preload failed for:', reference);
        }
      });
      
      return () => controller.abort();
    } catch (error) {
      console.debug('Bible preloading failed:', error);
    }
  }, [preloadBible, isAuthenticated]);

  // Preload audio resources
  const preloadAudioResources = useCallback(() => {
    if (!preloadAudio) return;
    
    // TODO: Add audio files when available
    // Currently disabled to prevent 404 errors
    console.log('[Preloader] Audio preloading disabled - no audio files available');
  }, [preloadAudio]);

  // Preload prayer data for authenticated users
  const preloadPrayerData = useCallback(async () => {
    if (!preloadPrayers) return;
    
    try {
      // Import PrayerService dynamically to avoid circular dependencies
      const { PrayerService } = await import('@/services/PrayerService');
      
      // Preload public prayers (limit to reduce bandwidth)
      await Promise.allSettled([
        PrayerService.listByVisibility('public'),
        // If authenticated, also preload user's prayers
        ...(isAuthenticated && user ? [PrayerService.listByAuthor(user.id)] : [])
      ]);
      
      console.debug('[Preloader] Prayer data preloaded successfully');
    } catch (error) {
      console.debug('Prayer data preloading failed:', error);
    }
  }, [preloadPrayers, isAuthenticated, user]);

  // Initialize preloading
  useEffect(() => {
    // Start preloading after a short delay to not interfere with critical loading
    const timer = setTimeout(() => {
      preloadRouteChunks();
      preloadAudioResources();
      
      if (isAuthenticated) {
        preloadBibleContent();
        preloadPrayerData();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, preloadRouteChunks, preloadAudioResources, preloadBibleContent, preloadPrayerData]);

  return {
    preloadRouteChunks,
    preloadBibleContent,
    preloadAudioResources,
    preloadPrayerData
  };
};